/**
 * Reporting for the standalone webpack bundles.
 *
 * webpack.common.js declares eight entries and only `main` loaded anything that
 * installed an error handler — and even there it sits inside
 * `if (NODE_ENV !== 'development' && sentryDsn)`. The other seven, which
 * include the two pages a person outside the company actually lands on (the QR
 * container page and the quote PIN screen), installed nothing at all: an
 * unhandled rejection was swallowed by the browser, so a failed request left a
 * spinner running and produced no console entry, no Sentry event and no clue.
 *
 * Those bundles also never call `Sentry.init`, so there is no client for a
 * report to go to. `ensureClient` creates one on first use, with the same DSN,
 * environment and release main.js uses, and stands down when main.js has
 * already initialised one (`getClient()` is then truthy) or when there is no
 * DSN — in development, or in a build where the variable is unset.
 *
 * This module only reports. It deliberately does not open a dialog: a global
 * net cannot know whether the rejection already produced one, and a duplicate
 * or spurious dialog on a customer-facing page is worse than the silence it
 * replaces. Clearing loading state still belongs in the individual .catch
 * handlers.
 *
 * Import for side effects, first thing in an entry file:
 *
 *     import '../bootstrap/global-error-handler';
 */

import { captureException, getClient, init } from '@sentry/vue';

const SENTRY_DSN = process.env.VUE_APP_ENV_SENTRY_DSN;

let clientChecked = false;
let clientReady = false;

function ensureClient() {
    if (clientChecked) return clientReady;
    clientChecked = true;

    // main.js initialises its own client for the `main` bundle; do not add a
    // second one on top of it.
    if (getClient()) {
        clientReady = true;
        return clientReady;
    }

    if (!SENTRY_DSN || process.env.NODE_ENV === 'development') {
        return clientReady;
    }

    init({
        dsn: SENTRY_DSN,
        environment: process.env.NODE_ENV,
        release: process.env.VUE_APP_ENV_RELEASE,
        include_local_variables: false,
        send_default_pii: false,
    });
    clientReady = true;
    return clientReady;
}

function report(error, kind) {
    if (ensureClient()) {
        captureException(error);
    }
    // Always leave a console entry: without a DSN this is the only record, and
    // with one it is what a developer looking at the page sees.
    // eslint-disable-next-line no-console
    console.error(`[${kind}]`, error);
}

if (typeof window !== 'undefined' && !window.__wdGlobalErrorHandlerInstalled) {
    window.__wdGlobalErrorHandlerInstalled = true;

    window.addEventListener('unhandledrejection', event => {
        report(event.reason, 'unhandledrejection');
    });

    window.addEventListener('error', event => {
        report(event.error || event.message, 'error');
    });
}
